import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Form, Button, Alert, Input, Divider } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import validationMessages from '../constants/ValidationMessages.json';
import { login, verifyMfaCode, clearMfaState } from '../actions/user';
import { getApiRoot } from '../constants/ParamountReactConstants';
import LoginFooter from './LoginFooter';
import FormPasswordInput from '../components/global/Forms/FormPasswordInput';
import FormInput from '../components/global/Forms/FormInput';
import SSOLoginButton from '../components/auth/SSOLoginButton';
import { useAppDispatch } from '../store/ReduxStore';
import { AsyncDispatch } from '../../types/global';
import { ApplicationState } from '../reducers';

const LoginSchema = Yup.object().shape({
  email: Yup.string().required(validationMessages.required),
  password: Yup.string().required(validationMessages.required),
});

type LoginPageProps = {
  okta?: boolean;
};

const LoginPage: React.FC<LoginPageProps> = ({ okta }) => {
  const { t } = useTranslation();
  const dispatch: AsyncDispatch = useAppDispatch();
  const [error, setError] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaSubmitting, setMfaSubmitting] = useState(false);
  const isProduction = process.env.REACT_APP_STAGE === 'production';

  const { mfaRequired, mfaSession, mfaEmail, mfaError } = useSelector(
    (state: ApplicationState) => ({
      mfaRequired: state.user.mfaRequired,
      mfaSession: state.user.mfaSession,
      mfaEmail: state.user.mfaEmail,
      mfaError: state.user.mfaError,
    })
  );

  const formMethods = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
    resolver: yupResolver(LoginSchema),
    mode: 'onTouched',
  });

  const { handleSubmit, formState } = formMethods;

  const onSubmit = async (values: { email: string; password: string }) => {
    setError(null);
    try {
      // @ts-ignore
      await dispatch(login(values.email, values.password));
    } catch (err: any) {
      const { message } = err.response?.data || {};
      setError(message || t('login:login_failed'));
    }
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaEmail || !mfaSession || !mfaCode) return;

    setMfaSubmitting(true);
    setError(null);

    try {
      // @ts-ignore
      await dispatch(verifyMfaCode(mfaEmail, mfaSession, mfaCode));
    } catch (err: any) {
      const { message, error: errMsg } = err.response?.data || {};
      setError(message || errMsg || t('login:mfaInvalidCode'));
    } finally {
      setMfaSubmitting(false);
    }
  };

  const handleBackToLogin = () => {
    dispatch(clearMfaState());
    setMfaCode('');
    setError(null);
  };

  const errorMessage = () => {
    const displayError = error || mfaError;
    if (displayError) {
      return <Alert type="error" showIcon message={displayError} style={{ marginBottom: 20 }} />;
    }
    return null;
  };

  if (mfaRequired) {
    return (
      <div className="login__background">
        <div className="login__box">
          <div className="login__box-header">
            <div className="login-logo" />
          </div>
          <div className="login__box-content">
            <div className="login--header-content">
              <div className="login__title">{t('login:mfaTitle')}</div>
              <div className="mt-1">{t('login:mfaDescription')}</div>
            </div>
            {errorMessage()}
            <form onSubmit={handleMfaSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">{t('login:mfaCodeLabel')}</label>
                <Input
                  prefix={<LockOutlined />}
                  size="large"
                  placeholder="000000"
                  value={mfaCode}
                  onChange={e => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  autoFocus
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </div>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                disabled={mfaCode.length !== 6 || mfaSubmitting}
                loading={mfaSubmitting}
                style={{ width: '100%', marginTop: 16 }}
              >
                {t('login:mfaVerify')}
              </Button>
              <div className="text-center mt-4">
                <Button type="link" onClick={handleBackToLogin}>
                  {t('login:backToLogin')}
                </Button>
              </div>
            </form>
          </div>
        </div>
        <LoginFooter />
      </div>
    );
  }

  return (
    <div className="login__background">
      <div className="login__box">
        <div className="login__box-header">
          <div className="login-logo" />
        </div>
        <div className="login__box-content">
          <div className="login--header-content">
            <div className="login__title">{t('login:login')}</div>
            <div className="mt-1">{t('login:welcome_txt1')}</div>
          </div>
          {errorMessage()}
          <FormProvider {...formMethods}>
            <Form onFinish={handleSubmit(onSubmit)} layout="vertical">
              <FormInput
                name="email"
                label={t('common:email')}
                placeholder={t('common:email')}
                size="large"
                required
                useHookForm
              />
              <FormPasswordInput
                name="password"
                label={t('common:password')}
                placeholder={t('common:password')}
                size="large"
                required
                useHookForm
              />
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                disabled={!formState.isValid || formState.isSubmitting}
                loading={formState.isSubmitting}
                style={{ width: '100%', marginTop: 24 }}
              >
                {t('login:login')}
              </Button>
              <div className="register__login-container forgot-password">
                <Link className="register__login-text" to="/forgot-password">
                  {t('login:forgotPassword')}
                </Link>
              </div>
              <div className="text-center">
                <Link className="register__login-text" to="/register">
                  {t('login:create_account')}
                </Link>
                {` ${t('login:register_txt')}`}
              </div>
              {!isProduction && (
                <>
                  <Divider plain>{t('common:or')}</Divider>
                  <SSOLoginButton />
                </>
              )}
              {okta && (
                <div className="text-center mt-3">
                  <Button href={`${getApiRoot()}/auth/saml`}>{t('login:oktaLogin')}</Button>
                </div>
              )}
            </Form>
          </FormProvider>
        </div>
      </div>

      <LoginFooter />
    </div>
  );
};

export default LoginPage;
